/**
 * Canonical model-facing instant format. Civil dates, durations, cron values,
 * and source-domain timestamp payloads intentionally do not use this contract.
 */
export const UTC_ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?Z$/

const OFFSET_TIMESTAMP_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})$/

export type EpochTimestampUnit = 'milliseconds' | 'seconds'

export interface NormalizeUtcTimestampOptions {
  /**
   * Numeric timestamps are accepted only at source boundaries whose unit is
   * explicitly known. Omitting this rejects numbers rather than guessing.
   */
  epochUnit?: EpochTimestampUnit
}

export function isUtcIsoTimestamp(value: unknown): value is string {
  if (typeof value !== 'string' || !UTC_ISO_TIMESTAMP_PATTERN.test(value)) return false
  const epochMs = Date.parse(value)
  if (!Number.isFinite(epochMs)) return false

  // Date.parse normalizes some impossible calendar values instead of rejecting
  // them (for example February 30). Compare the parsed UTC calendar components
  // to the input while allowing any supported fractional-second precision.
  return new Date(epochMs).toISOString().slice(0, 19) === value.slice(0, 19)
}

export function requireUtcIsoTimestamp(value: unknown, fieldName: string): string {
  if (!isUtcIsoTimestamp(value)) {
    throw new Error(
      `${fieldName} must be a valid UTC ISO-8601 timestamp ending in Z (for example, 2026-09-01T15:49:00.000Z).`
    )
  }
  return value
}

export function optionalUtcIsoTimestamp(value: unknown, fieldName: string): string | undefined {
  if (value === undefined || value === null) return undefined
  return requireUtcIsoTimestamp(value, fieldName)
}

/**
 * Normalize one curated upstream instant for a model-facing tool result.
 *
 * Offset-bearing RFC 3339 values are converted to UTC. Epoch values require an
 * explicit source unit. Offset-free strings are rejected because they do not
 * identify an instant.
 */
export function normalizeUtcIsoTimestamp(
  value: unknown,
  fieldName: string,
  options: NormalizeUtcTimestampOptions = {}
): string {
  if (value instanceof Date) {
    if (!Number.isFinite(value.getTime())) throw invalidSourceTimestamp(fieldName)
    return value.toISOString()
  }

  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    if (options.epochUnit === undefined) throw ambiguousEpochTimestamp(fieldName)
    const numeric = typeof value === 'number' ? value : Number(value)
    if (!Number.isSafeInteger(numeric)) throw invalidSourceTimestamp(fieldName)
    const epochMs = options.epochUnit === 'seconds' ? numeric * 1000 : numeric
    const date = new Date(epochMs)
    if (!Number.isFinite(date.getTime())) throw invalidSourceTimestamp(fieldName)
    return date.toISOString()
  }

  if (typeof value !== 'string') throw invalidSourceTimestamp(fieldName)
  const match = OFFSET_TIMESTAMP_PATTERN.exec(value)
  if (!match || !hasRealCalendarComponents(match)) throw ambiguousSourceTimestamp(fieldName)
  const epochMs = Date.parse(value)
  if (!Number.isFinite(epochMs)) throw invalidSourceTimestamp(fieldName)

  const utc = new Date(epochMs).toISOString()
  const fraction = match[7]
  return fraction === undefined ? utc : `${utc.slice(0, 19)}.${fraction.padEnd(3, '0')}Z`
}

export function normalizeOptionalUtcIsoTimestamp(
  value: unknown,
  fieldName: string,
  options: NormalizeUtcTimestampOptions = {}
): string | undefined {
  if (value === undefined || value === null || value === '') return undefined
  return normalizeUtcIsoTimestamp(value, fieldName, options)
}

export function assertUtcTimestampRange(
  start: string | undefined,
  end: string | undefined,
  options: {
    startField: string
    endField: string
    allowEqual?: boolean
  }
): void {
  if (start === undefined || end === undefined) return
  const startMs = Date.parse(start)
  const endMs = Date.parse(end)
  const ordered = options.allowEqual === true ? startMs <= endMs : startMs < endMs
  if (!ordered) {
    const relation = options.allowEqual === true ? 'before or equal to' : 'strictly before'
    throw new Error(`${options.startField} must be ${relation} ${options.endField}.`)
  }
}

function hasRealCalendarComponents(match: RegExpExecArray): boolean {
  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])
  const hour = Number(match[4])
  const minute = Number(match[5])
  const second = Number(match[6])
  if (month < 1 || month > 12 || hour > 23 || minute > 59 || second > 59) return false
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate()
  if (day < 1 || day > daysInMonth) return false
  if (match[8] !== 'Z') {
    const [offsetHour, offsetMinute] = match[8].slice(1).split(':').map(Number)
    if (offsetHour > 23 || offsetMinute > 59) return false
  }
  return true
}

function invalidSourceTimestamp(fieldName: string): Error {
  return new Error(`${fieldName} is not a valid timestamp instant.`)
}

function ambiguousSourceTimestamp(fieldName: string): Error {
  return new Error(
    `${fieldName} must include Z or a numeric UTC offset; an offset-free source time cannot be normalized safely.`
  )
}

function ambiguousEpochTimestamp(fieldName: string): Error {
  return new Error(
    `${fieldName} is numeric but its source unit was not declared; refusing to guess seconds versus milliseconds.`
  )
}
